import { Injectable, OnInit, inject } from "@angular/core";
import { getSelectorFromGroup, loadComponentTokens } from "@wavemaker/foundation-css";
import { FileService, ProjectService } from '@wms/services';
import { BaseComponent } from '@wms/app/components';
import { FoundationService } from "@wms/app/workspace/style-workspace/foundation/services/foundationService/foundation.service";
import { DESIGN_TOKENS_DIR_NAME, TOKENS_KEYS } from "@wms/app/core";
import { runtimeEventManager } from "@wm/core";

@Injectable({ providedIn: 'root' })
export class WidgetStylingService extends BaseComponent implements OnInit {
  private appearanceVariantCache: Record<string, any> = {};
  private selectorCache: Record<string, string[]> = {};
  override projectService = inject(ProjectService);
  private foundationService = inject(FoundationService);
  projectResourcesTree: any = [];

  constructor(private fileService: FileService) {
    super();
    runtimeEventManager.setService('widgetStylingService', this);
    this.registerDestroyListener.add(
      this.foundationService.tokensCleared$.subscribe(() => {
        this.clearCache();
      })
    );
  }

  ngOnInit(): void {
  }

  /**
  * Loads the project resource data through API call to file service
  * transform the data into the data that the tree component accepts
  * feed the transformed data to this.projectResourceTree
  */
  async loadProjectResourcesTree(): Promise<void> {
    try {
      const data = await this.fileService.readDir(`../project/src/main/webapp/${DESIGN_TOKENS_DIR_NAME}/`);
      this.projectResourcesTree = data;
    } catch (error) {
      this.projectResourcesTree = [];
    }
  }

  /**
   * Extract appearance and variant map
   */
  async extractAppearanceVariantMap(componentName: string): Promise<Record<string, any>> {

    // Return cached result if available
    if (this.appearanceVariantCache[componentName]) {
      return this.appearanceVariantCache[componentName];
    }
    if (!componentName) return {};
    try {
      const appearanceMap = await this.getAppearanceVariantSelectors(componentName);
      this.appearanceVariantCache[componentName] = appearanceMap;
      return appearanceMap;
    } catch (error) {
      return {};
    }
  }

  /**
   * Get variant classes
   */
  getVariantSelectorFromCache(componentName: string, appearance: string, variant?: string): string[] {
    // Create cache key for this specific combination
    const cacheKey = `${componentName}:${appearance}:${variant}`;
    // Return cached result if available
    if (this.selectorCache[cacheKey]) {
      return this.selectorCache[cacheKey];
    }

    const appearanceMap = this.appearanceVariantCache[componentName];
    if (!appearanceMap || !appearanceMap[appearance]) {
      return [];
    }
    const selector = variant ? appearanceMap?.[appearance]?.variants?.[variant]?.selector : appearanceMap?.[appearance]?.selector;

    if (!selector) {
      return [];
    }

    let classList: string[] = [];
    if (selector) {
      // Optimized selector processing - single operation
      classList = selector
        .replace(/\./g, ' ')
        .trim()
        .split(/\s+/)
        .filter((cls: string) => cls.length > 0);

      if (classList.length > 0) {
        this.selectorCache[cacheKey] = classList;
      }
    }
    return classList;
  }

  /**
   * Clear all caches (useful for testing or memory management)
   */
  clearCache(): void {
    this.appearanceVariantCache = {};
    this.selectorCache = {};
  }

  /**
 * Retrieves merged appearance and variant selectors for a given component,
 * including overrides from project-specific tokens if available.
 */
  async getAppearanceVariantSelectors(componentName: string): Promise<Record<string, any>> {
    const isMobile = this.projectService.isNativeMobileApplicationType();
    const platform = isMobile ? 'native_mobile' : 'web'; // Used by selector logic
    const tokenPlatform = isMobile ? 'mobile' : 'web';   // Used for token file path resolution

    await this.loadProjectResourcesTree();

    // Load base tokens
    const module = await loadComponentTokens(componentName, platform);
    const baseTokenData = module.default;
    const componentKey = Object.keys(baseTokenData)[0];
    const baseComponent = baseTokenData[componentKey];

    // Load project-specific override tokens
    const overrideData = await this.loadOverrideTokens(componentName);
    const overrideComponent = overrideData?.[componentKey] || {};

    const baseAppearances = baseComponent?.appearances || {};
    const overrideAppearances = overrideComponent?.appearances || {};
    const overrideMetaAppearances = overrideComponent?.meta?.appearances || {};

    let result: Record<string, any> = {};
    const baseKeys = Object.keys(baseAppearances);
    const overrideKeys = Object.keys(overrideAppearances);

    // Merge appearance keys from both base and override
    const allAppearanceNames = new Set([
      ...baseKeys,
      ...overrideKeys
    ]);

    if (baseKeys.length === 0) {
      result[TOKENS_KEYS.STANDARD] = { selector: "", variants: [] };
    }

    let customAppearancesPresent = false;
    result["custom"] = {selector : '', source: 'user', variants: {}} 

    // Process each appearance
    for (const appearance of allAppearanceNames) {
      const baseAppearance = baseAppearances[appearance] || {};
      const overrideAppearance = overrideAppearances[appearance] || {};
      const overrideMeta = overrideMetaAppearances[appearance] || {};
      const source = overrideMeta?.source || ""

      // Merge variant groups from base and override
      const variantGroups = {
        ...(baseAppearance.variantGroups || {}),
        ...(overrideAppearance.variantGroups || {})
      };

      const variants: Record<string, { selector: string , source?: string }> = {};
      // Process each variant in the group
      for (const groupName in variantGroups) {
        const baseVariants = baseAppearance.variantGroups?.[groupName] || {};
        const overrideVariants = overrideAppearance.variantGroups?.[groupName] || {};
        const groupVariants = { ...baseVariants, ...overrideVariants };

        for (const variant of Object.keys(groupVariants)) {
          const selector = this.getBlockSelector(
            { type: 'variant', component: componentKey, appearance, variant, groupType: groupName, componentJson: baseTokenData },
            tokenPlatform
          );
          variants[variant] = { selector, source };
        }
      }

      // Get appearance-level selector
      const appearanceSelector = this.getBlockSelector(
        { type: 'mapping', component: componentKey, appearance, isRootMapping: false, componentJson: baseTokenData },
        tokenPlatform
      );

      if (source) {
        result["custom"]["variants"][appearance] =  { selector: appearanceSelector, source};
        customAppearancesPresent = true
      } else {
        result[appearance] = { selector: appearanceSelector, variants , source};
      }
    }

    if (customAppearancesPresent) {
      result = {
        custom: result['custom'],
        ...result
      };
    } else {
      delete result['custom'];
    }

    return result;
  }

  async loadOverrideTokens(category: string): Promise<any> {
    const fileName = `${category}.json`;
    const targetPath = `${this.foundationService.projectOverridePath}/${this.locale.LABEL_STYLE_WIDGETS.toLowerCase()}/${category}/${fileName}`;
    const overrideUrl = `${this.foundationService.overridePath}/${this.locale.LABEL_STYLE_WIDGETS.toLowerCase()}/${category}/${fileName}`;
    if (!this.foundationService.hasFileInProject(this.projectResourcesTree, targetPath)) {
      return {};
    }
    try {
      return await this.foundationService.loadFile(overrideUrl);
    } catch {
      return {};
    }
  }

  getBlockSelector(config: any, platfrom: string): string {
    const { type, component, appearance, variant, groupType, state, isRootMapping, componentJson } = config;

    return (
      getSelectorFromGroup(
        { type, component, appearance, variant, groupType, state, isRootMapping },
        { unfilteredTokens: { ...componentJson } },
        platfrom
      ) || ':root'
    );
  }

  override ngOnDestroy(): void {
  }
}
